import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", email: "", message: "" };

export default function EmailDoctorModal({ open, onClose, heading, subtext, initialMessage }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm((c) => ({ ...c, message: initialMessage || "" }));
  }, [open, initialMessage]);

  if (!open) return null;

  const set = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please enter your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent to Dr. Louden.");
      setForm(EMPTY);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm">
      <div
        data-testid="email-doctor-modal"
        className="w-full max-w-xl rounded-2xl border border-[#7C3AED]/30 bg-[#080808] p-6 shadow-2xl md:p-8"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">
              Direct Message
            </span>
            <h3 className="mt-2 font-display text-3xl uppercase tracking-tight text-white">
              {heading || "Email Dr. Louden"}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/55">
              {subtext || "Your message is sent directly to Deltadlenterprise2026@gmail.com."}
            </p>
          </div>
          <button
            type="button"
            data-testid="email-doctor-close"
            onClick={onClose}
            aria-label="Close email form"
            className="rounded-full border border-white/15 p-2 text-white/60 transition-colors hover:bg-white hover:text-black"
          >
            <X size={18} />
          </button>
        </div>

        <form data-testid="email-doctor-form" onSubmit={submit} className="mt-7 space-y-5">
          <input
            data-testid="email-doctor-name"
            className="line-input"
            placeholder="Your name"
            value={form.name}
            onChange={set("name")}
            maxLength={120}
          />
          <input
            data-testid="email-doctor-email"
            type="email"
            className="line-input"
            placeholder="Your email address"
            value={form.email}
            onChange={set("email")}
          />
          <textarea
            data-testid="email-doctor-message"
            className="line-input min-h-32 resize-none"
            placeholder="Write your message to Dr. Louden"
            value={form.message}
            onChange={set("message")}
            maxLength={2000}
          />
          <button
            data-testid="email-doctor-submit"
            type="submit"
            disabled={sending}
            className="magnetic-btn w-full rounded-full bg-[#7C3AED] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
